// CATALOG1 (docs/build-briefs/CATALOG1-shared-product-catalog.md; DEC-613, DEC-614): the words and rows of Add item's "Find a
// product". Pure functions only: no DOM and no requests, so admin.ts owns the wiring and this file owns what a person reads.
// Every fact here is a product fact. The catalog never carries a price, cost, vendor, par, stock or package size, so none is shown.

import type { SharedCatalogProductDto } from "./api.ts";
import { abvBasisPointsToStrengthDisplayString, strengthFieldLabel } from "./strength.ts";

export const FIND_DEBOUNCE_MS = 200;
export const FIND_MIN_CHARS = 2;

export interface FindGroup {
  producer: string;
  products: SharedCatalogProductDto[];
}

/** The server already orders products by producer; this keeps that order and starts a new group when the producer changes. */
export function groupByProducer(products: readonly SharedCatalogProductDto[]): FindGroup[] {
  const groups: FindGroup[] = [];
  for (const product of products) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.producer === product.producer) last.products.push(product);
    else groups.push({ producer: product.producer, products: [product] });
  }
  return groups;
}

/** "Beer · 7.3%" for the result list: the kind's label, then the strength in the unit the kind uses. */
export function strengthText(
  product: Pick<SharedCatalogProductDto, "abvBasisPoints" | "itemKind">,
): string | null {
  if (product.abvBasisPoints === null) return null;
  const value = abvBasisPointsToStrengthDisplayString(product.abvBasisPoints, product.itemKind);
  return product.itemKind === "liquor" ? `${value} proof` : `${value}%`;
}

export function resultHint(product: SharedCatalogProductDto, kindLabel: string): string {
  return [kindLabel, strengthText(product)]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/** The label of the "+ New" row: with exactly one producer in the results it names that producer. */
export function newProductLabel(groups: readonly FindGroup[]): string {
  return groups.length === 1 ? `+ New ${groups[0]!.producer} product` : "+ New product";
}

export interface FindPreviewRow {
  field: string;
  value: string;
  /** Where the value came from. Only the catalog until CATALOGAI1 adds the AI lookup. */
  from: "Catalog" | "AI";
}

export interface FindPreviewContext {
  kindLabel: string;
  /** The label of the category when the customer has it; null when the customer's list does not offer it. */
  categoryLabel: string | null;
  /** True when the customer offers this kind, so it can be filled. */
  kindOffered: boolean;
  /** True only for liquor at a location in North Carolina. */
  showNcCode: boolean;
}

/** Every value that will be filled, in the order of the Add item form. A fact the catalog does not have is left out, not shown as a dash. */
export function previewRows(
  product: SharedCatalogProductDto,
  context: FindPreviewContext,
): FindPreviewRow[] {
  const from = product.source === "ai" ? "AI" : "Catalog";
  const rows: FindPreviewRow[] = product.producer.trim() === "" ? [] : [{ field: "Producer", value: product.producer, from }];
  if (context.kindOffered) rows.push({ field: "Type", value: context.kindLabel, from });
  if (product.category !== null && context.categoryLabel !== null) {
    rows.push({ field: "Category", value: context.categoryLabel, from });
  }
  const strength = strengthText(product);
  if (strength !== null) {
    rows.push({
      field: strengthFieldLabel(product.itemKind),
      value: strength.replace(/ proof$/, ""),
      from,
    });
  }
  if (product.countryRegion !== null)
    rows.push({ field: "Country / region", value: product.countryRegion, from });
  if (product.upc !== null) rows.push({ field: "Barcode", value: product.upc, from });
  if (product.ncCode !== null && context.showNcCode)
    rows.push({ field: "NC code", value: product.ncCode, from });
  return rows;
}

/** One sentence under the table for a fact that will not be filled and the reason, so nothing disappears silently. */
export function previewNotice(
  product: SharedCatalogProductDto,
  context: FindPreviewContext,
): string {
  const left: string[] = [];
  if (!context.kindOffered)
    left.push(`the type "${context.kindLabel}" is not one of your item types`);
  if (product.category !== null && context.categoryLabel === null)
    left.push("the category is not on your list");
  return left.length === 0
    ? "These will be filled in. Nothing is saved until you finish."
    : `These will be filled in. Left for you to choose: ${left.join(" and ")}.`;
}

// CATALOGAI2: the category Add item picks for a product. Only ever one of the location's own categories for the item's kind.
// The AI names a category in its own words (it called "Gilde Oktoberfest" a Belgian), so for a beer, wine or cider the words of
// the product's name come first (Oktoberfest in "Gilde Oktoberfest"), then the category the product carries, then a style that
// means the same thing (Marzen, Festbier). Liquor keeps the carried category first: a name such as "Rum Chata" is not a rum.
// A product with no match picks nothing, so the person's own choice stays.
const STYLE_SYNONYMS: Readonly<Record<string, readonly string[]>> = {
  marzen: ["oktoberfest"],
  festbier: ["oktoberfest"],
  oktoberfestbier: ["oktoberfest"],
};

function fold(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/ae(?=r)/g, "a");
}
function wordsOf(text: string): string[] {
  return fold(text).split(/[^a-z0-9]+/).filter((word) => word.length > 0);
}

export function chooseLocationCategory(
  options: readonly { value: string; label: string }[],
  product: { productName: string; category: string | null; itemKind: string },
): { value: string; label: string } | undefined {
  const squash = (text: string): string => fold(text).replace(/[^a-z0-9]+/g, "");
  const exactCarried = (): { value: string; label: string } | undefined => {
    if (product.category === null) return undefined;
    const wanted = squash(product.category);
    return options.find((entry) => squash(entry.value) === wanted || squash(entry.label) === wanted);
  };
  // The option whose every word is among the words, the longest label first so "Pale Ale" beats "Ale".
  const byWords = (words: readonly string[]): { value: string; label: string } | undefined => {
    const have = new Set(words);
    return [...options]
      .map((entry) => ({ entry, own: wordsOf(entry.label) }))
      .filter(({ own }) => own.length > 0 && own.every((word) => have.has(word)))
      .sort((a, b) => b.own.length - a.own.length || b.entry.label.length - a.entry.label.length)[0]?.entry;
  };
  const nameWords = wordsOf(product.productName);
  const carriedWords = product.category === null ? [] : wordsOf(product.category.replace(/_/g, " "));
  const synonyms = [...nameWords, ...carriedWords].flatMap((word) => STYLE_SYNONYMS[word] ?? []);
  const fromName = byWords(nameWords);
  if (product.itemKind !== "liquor" && fromName !== undefined) return fromName;
  return (
    exactCarried() ??
    (product.itemKind === "liquor" ? fromName : undefined) ??
    byWords(synonyms) ??
    (carriedWords.length > 0 ? byWords(carriedWords) : undefined)
  );
}
